import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardHeader,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  sanitizeIdleTimeoutMinutes,
  DEFAULT_IDLE_TIMEOUT_MINUTES,
  MIN_IDLE_TIMEOUT_MINUTES,
  MAX_IDLE_TIMEOUT_MINUTES,
} from '../contexts/IdleTimerContext';

const UpdateProfilePage = () => {
  const navigate = useNavigate();
  const { userProfile, fetchProfile } = useUser();
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: ''
  });
  
  const [originalData, setOriginalData] = useState({
    email: '',
    full_name: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  // Business profile state (managers only)
  const isManager = userProfile?.role === 'manager';
  const [businessProfile, setBusinessProfile] = useState({
    business_name: '',
    business_id: '',
    business_type: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    logo_url: '',
  });
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessError, setBusinessError] = useState('');
  const [businessSuccess, setBusinessSuccess] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);

  // Session timeout (idle auto-logout), per business. Managers edit it;
  // employees see it read-only. Held as a string so the field can be cleared
  // while typing.
  const [sessionTimeout, setSessionTimeout] = useState(String(DEFAULT_IDLE_TIMEOUT_MINUTES));
  const [savedSessionTimeout, setSavedSessionTimeout] = useState(String(DEFAULT_IDLE_TIMEOUT_MINUTES));
  const [sessionFieldError, setSessionFieldError] = useState('');
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [sessionSuccess, setSessionSuccess] = useState('');
  
  // Form validation
  const [errors, setErrors] = useState({
    email: '',
    full_name: ''
  });

  // Initialize form with user data
  useEffect(() => {
    if (userProfile) {
      const userData = {
        email: userProfile.email || '',
        full_name: userProfile.full_name || ''
      };
      setFormData(userData);
      setOriginalData(userData);
      setInitialLoading(false);

      // Applies to every role: employees see the value read-only.
      const initialTimeout = String(
        sanitizeIdleTimeoutMinutes(userProfile.ui_config?.session_timeout_minutes)
      );
      setSessionTimeout(initialTimeout);
      setSavedSessionTimeout(initialTimeout);

      // Load business profile for managers
      if (userProfile.role === 'manager' && userProfile.group?.business_profile) {
        setBusinessProfile(prev => ({
          ...prev,
          ...userProfile.group.business_profile,
        }));
      }
    }
  }, [userProfile]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      email: '',
      full_name: ''
    };

    // Email validation
    if (!formData.email) {
      newErrors.email = 'נדרש להזין כתובת אימייל';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'יש להזין כתובת אימייל תקינה';
      isValid = false;
    }

    // Full name validation
    if (!formData.full_name) {
      newErrors.full_name = 'נדרש להזין שם מלא';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const handleReset = () => {
    setFormData(originalData);
    setErrors({
      email: '',
      full_name: ''
    });
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setErrorMsg('');
    setSuccessMsg('');
    
    if (!validateForm()) {
      return;
    }
    
    // Check if anything actually changed
    if (formData.email === originalData.email && formData.full_name === originalData.full_name) {
      setSuccessMsg('לא בוצעו שינויים');
      return;
    }
    
    setLoading(true);
    
    try {
      await API.put('/user/update-profile/', {
        email: formData.email,
        full_name: formData.full_name
      });
      
      // Show success message
      setSuccessMsg('הפרטים האישיים עודכנו בהצלחה!');
      
      // Wait a bit before refreshing user profile data
      setTimeout(async () => {
        try {
          await fetchProfile();
          // Update originalData to match the new data
          setOriginalData({
            email: formData.email,
            full_name: formData.full_name
          });
        } catch (error) {
          console.error("Error refreshing profile:", error);
        }
      }, 1500); // Wait 1.5 seconds so user can see the success message
      
    } catch (error) {
      // Show error message
      setErrorMsg(error.response?.data?.error || 'אירעה שגיאה בעת עדכון הפרטים');
    } finally {
      setLoading(false);
    }
  };

  // Business profile handlers
  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleBusinessSubmit = async () => {
    setBusinessError('');
    setBusinessSuccess('');
    setBusinessLoading(true);
    try {
      await API.put('/business-profile/', businessProfile);
      setBusinessSuccess('פרטי העסק עודכנו בהצלחה!');
      setTimeout(() => { fetchProfile(); }, 1500);
    } catch (error) {
      setBusinessError(error.response?.data?.error || 'שגיאה בעדכון פרטי העסק');
    } finally {
      setBusinessLoading(false);
    }
  };

  // Session timeout handlers
  const validateSessionTimeout = () => {
    const trimmed = sessionTimeout.trim();
    if (!trimmed) {
      setSessionFieldError('נדרש להזין מספר דקות');
      return false;
    }
    // Rejects '-5', '7.5' and '1e3', which type="number" would otherwise allow.
    if (!/^\d+$/.test(trimmed)) {
      setSessionFieldError('יש להזין מספר שלם של דקות');
      return false;
    }
    const value = parseInt(trimmed, 10);
    if (value < MIN_IDLE_TIMEOUT_MINUTES || value > MAX_IDLE_TIMEOUT_MINUTES) {
      setSessionFieldError(`הערך חייב להיות בין ${MIN_IDLE_TIMEOUT_MINUTES} ל-${MAX_IDLE_TIMEOUT_MINUTES} דקות`);
      return false;
    }
    setSessionFieldError('');
    return true;
  };

  const handleSessionTimeoutSubmit = async () => {
    setSessionError('');
    setSessionSuccess('');

    if (!validateSessionTimeout()) return;

    const value = parseInt(sessionTimeout.trim(), 10);
    if (String(value) === savedSessionTimeout) {
      setSessionSuccess('לא בוצעו שינויים');
      return;
    }

    setSessionLoading(true);
    try {
      await API.put('/settings/session-timeout/', { session_timeout_minutes: value });
      setSavedSessionTimeout(String(value));
      setSessionSuccess('זמן הניתוק האוטומטי עודכן בהצלחה!');
      // Not cosmetic: refreshing the profile pushes the new ui_config into
      // UserContext, which re-runs the IdleTimerProvider effects — so the new
      // window applies in this tab without a reload.
      setTimeout(() => { fetchProfile(); }, 1500);
    } catch (error) {
      setSessionError(error.response?.data?.error || 'שגיאה בעדכון זמן הניתוק');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    setBusinessError('');
    try {
      const formDataLogo = new FormData();
      formDataLogo.append('logo', file);
      const { data } = await API.post('/business-profile/logo/', formDataLogo, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBusinessProfile(prev => ({ ...prev, logo_url: data.logo_url }));
      setBusinessSuccess('הלוגו הועלה בהצלחה!');
      setTimeout(() => { fetchProfile(); }, 1500);
    } catch (error) {
      setBusinessError(error.response?.data?.error || 'שגיאה בהעלאת הלוגו');
    } finally {
      setLogoUploading(false);
    }
  };

  // Show loading while fetching initial data
  if (initialLoading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <div dir="rtl">
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          עדכון פרטים אישיים
        </Typography>
        
        <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
          <CardHeader
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              py: 1.5,
            }}
            title={
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                פרטי המשתמש
              </Typography>
            }
          />
          
          <CardContent>
            {/* Show either error or success message, not both */}
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            )}
            
            {successMsg && !errorMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMsg}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {/* Username - Read only */}
              <TextField
                margin="normal"
                fullWidth
                label="שם משתמש"
                value={userProfile?.username || ''}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
              
              {/* Email */}
              <TextField
                margin="normal"
                required
                fullWidth
                label="כתובת אימייל"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
              />
              
              {/* Full Name */}
              <TextField
                margin="normal"
                required
                fullWidth
                label="שם מלא"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                error={!!errors.full_name}
                helperText={errors.full_name}
                disabled={loading}
              />
              
              <Divider sx={{ my: 3 }} />
              
              {/* Role and Group - Read only */}
              <TextField
                margin="normal"
                fullWidth
                label="תפקיד"
                value={userProfile?.role === 'manager' ? 'מנהל' : userProfile?.role === 'employee' ? 'עובד' : userProfile?.role || ''}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
              
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  ביטול
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                >
                  איפוס שינויים
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'מעדכן...' : 'שמור שינויים'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        {/* Business Profile Section - Managers Only */}
        {isManager && (
          <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
            <CardHeader
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon />
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                    פרטי העסק
                  </Typography>
                </Box>
              }
              subheader={
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  פרטים אלו יופיעו בראש כל מסמך מודפס
                </Typography>
              }
            />
            <CardContent sx={{ pt: 3 }}>
              {businessError && <Alert severity="error" sx={{ mb: 2 }}>{businessError}</Alert>}
              {businessSuccess && !businessError && <Alert severity="success" sx={{ mb: 2 }}>{businessSuccess}</Alert>}

              {/* Logo Section */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 3, mb: 3,
                p: 2, borderRadius: 2, backgroundColor: '#f8f9fa', border: '1px dashed #ccc',
              }}>
                <Avatar
                  src={businessProfile.logo_url}
                  sx={{
                    width: 90, height: 90,
                    border: '2px solid #e0e0e0',
                    backgroundColor: '#fff',
                  }}
                  variant="rounded"
                >
                  <BusinessIcon sx={{ fontSize: 40, color: '#bbb' }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>לוגו העסק</Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoUpload}
                    disabled={logoUploading}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={logoUploading ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                      disabled={logoUploading}
                      size="small"
                      sx={{ direction: 'ltr' }}
                    >
                      {logoUploading ? 'מעלה...' : businessProfile.logo_url ? 'החלף לוגו' : 'העלה לוגו'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    מומלץ: תמונה ברורה בגודל 200x200 פיקסלים לפחות
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Business Details */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>פרטים עיקריים</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="שם העסק" name="business_name"
                    value={businessProfile.business_name} onChange={handleBusinessChange}
                    disabled={businessLoading}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth label="ח.פ / ע.מ" name="business_id"
                    value={businessProfile.business_id} onChange={handleBusinessChange}
                    disabled={businessLoading}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth disabled={businessLoading}>
                    <InputLabel>סוג עסק</InputLabel>
                    <Select
                      name="business_type" label="סוג עסק"
                      value={businessProfile.business_type} onChange={handleBusinessChange}
                    >
                      <MenuItem value="">לא צוין</MenuItem>
                      <MenuItem value="עוסק מורשה">עוסק מורשה</MenuItem>
                      <MenuItem value="עוסק פטור">עוסק פטור</MenuItem>
                      <MenuItem value="חברה בע״מ">חברה בע״מ</MenuItem>
                      <MenuItem value="שותפות">שותפות</MenuItem>
                      <MenuItem value="עמותה">עמותה</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>פרטי התקשרות</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="כתובת" name="address"
                    value={businessProfile.address} onChange={handleBusinessChange}
                    disabled={businessLoading}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth label="טלפון" name="phone"
                    value={businessProfile.phone} onChange={handleBusinessChange}
                    disabled={businessLoading}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth label="פקס" name="fax"
                    value={businessProfile.fax} onChange={handleBusinessChange}
                    disabled={businessLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth label='דוא"ל העסק' name="email" type="email"
                    value={businessProfile.email} onChange={handleBusinessChange}
                    disabled={businessLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth label="אתר אינטרנט" name="website"
                    value={businessProfile.website} onChange={handleBusinessChange}
                    disabled={businessLoading}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleBusinessSubmit}
                  disabled={businessLoading}
                  size="large"
                >
                  {businessLoading ? 'שומר...' : 'שמור פרטי עסק'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ── Session timeout (idle auto-logout) ── */}
        <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
          <CardHeader
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              py: 1.5,
            }}
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon />
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  ניתוק אוטומטי
                </Typography>
              </Box>
            }
            subheader={
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                הגדרת זמן חוסר פעילות עד ניתוק אוטומטי מהמערכת
              </Typography>
            }
          />
          <CardContent sx={{ pt: 3 }}>
            {sessionError && <Alert severity="error" sx={{ mb: 2 }}>{sessionError}</Alert>}
            {sessionSuccess && !sessionError && <Alert severity="success" sx={{ mb: 2 }}>{sessionSuccess}</Alert>}

            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              '& > *': { flex: { xs: '1 1 100%', sm: '0 1 280px' } },
            }}>
              <TextField
                fullWidth
                type="number"
                label="זמן ניתוק אוטומטי (בדקות)"
                value={sessionTimeout}
                onChange={(e) => { setSessionTimeout(e.target.value); setSessionFieldError(''); }}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                inputProps={{
                  min: MIN_IDLE_TIMEOUT_MINUTES,
                  max: MAX_IDLE_TIMEOUT_MINUTES,
                  step: 1,
                  inputMode: 'numeric',
                }}
                disabled={sessionLoading || !isManager}
                error={!!sessionFieldError}
                helperText={sessionFieldError || `ברירת המחדל היא ${DEFAULT_IDLE_TIMEOUT_MINUTES} דקות (טווח ${MIN_IDLE_TIMEOUT_MINUTES}-${MAX_IDLE_TIMEOUT_MINUTES}).`}
                sx={{
                  '& input': {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
              />
            </Box>

            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              דקה לפני הניתוק תוצג התראה עם ספירה לאחור, וכל פעולה מאפסת את הספירה.
              ההגדרה חלה על כל משתמשי העסק ותיכנס לתוקף עבורם בכניסה הבאה או ברענון הדף.
              אינה חלה על אפליקציית המובייל המותקנת, שנשארת מחוברת עד פקיעת ההרשאה.
            </Typography>

            {!isManager && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                רק מנהל יכול לשנות הגדרה זו.
              </Typography>
            )}

            {isManager && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSessionTimeoutSubmit}
                    disabled={sessionLoading}
                    size="large"
                  >
                    {sessionLoading ? 'שומר...' : 'שמור הגדרה'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default UpdateProfilePage;